import { Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap, take } from 'rxjs';
import { GlobalErrorService } from '@services/global-error.service';
import { environment } from '@environments/environment';
import { VendorService } from '@services/vendor.service';

export interface VendorCandidatesQuery {
  page?: number;
  limit?: number;
  q?: string;
  status?: string;
  jobTitle?: string;
  location?: string;
  candidateName?: string;
  sort?: string;
  dir?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CandidatesService {
  private baseUrl = environment.apiUrl;
  candidatesSignal: WritableSignal<any> = signal(null);

  constructor(
    private http: HttpClient,
    private errorHandler: GlobalErrorService,
    private vendorService: VendorService,
  ) {}

  /**
   * Fetch a page of the caller's own candidates. Vendor scope is validated
   * server-side. Any filter/search/sort triggers the spec path (search=true).
   */
  getCandidates(query: VendorCandidatesQuery = {}): void {
    this.vendorService.currentVendor$
      .pipe(
        take(1),
        switchMap((currentVendor: any) => {
          let params = new HttpParams()
            .set('page', String(query.page ?? 1))
            .set('limit', String(query.limit ?? 10));

          const hasCriteria =
            !!(query.q || query.status || query.jobTitle || query.location ||
               query.candidateName || query.sort);
          if (hasCriteria) {
            params = params.set('search', 'true');
            if (query.q) params = params.set('q', query.q);
            if (query.status) params = params.set('status', query.status);
            if (query.jobTitle) params = params.set('jobTitle', query.jobTitle);
            if (query.location) params = params.set('location', query.location);
            if (query.candidateName) params = params.set('candidateName', query.candidateName);
            if (query.sort) params = params.set('sort', query.sort);
            if (query.dir) params = params.set('dir', query.dir);
          }
          if (currentVendor?.id) {
            params = params.set('vendorId', currentVendor.id);
          }

          return this.http.get<any>(`${this.baseUrl}/vendor/candidates`, { params });
        }),
        catchError((error) => {
          this.errorHandler.logError(error);
          return of(null);
        }),
      )
      .subscribe((response) => {
        this.candidatesSignal.set(response ?? null);
      });
  }

  /** Distinct filter facet values (status / jobTitle / location) for the vendor. */
  getCandidateFacets(): Observable<{ [key: string]: string[] }> {
    return this.vendorService.currentVendor$.pipe(
      take(1),
      switchMap((currentVendor: any) => {
        let params = new HttpParams();
        if (currentVendor?.id) params = params.set('vendorId', currentVendor.id);
        return this.http
          .get<{ data: { [key: string]: string[] } }>(
            `${this.baseUrl}/vendor/candidates/facets`,
            { params },
          )
          .pipe(map((res) => res?.data ?? {}));
      }),
      catchError((error) => {
        this.errorHandler.logError(error);
        return of({});
      }),
    );
  }
}
